import type {
  ContentState,
  CustomerStatus,
  OrderStatus,
  ProductStatus,
  ShopperHiddenReason,
} from '@avrahami/contracts';
import { Badge, StatusBadge, type BadgeTone } from '@avrahami/ui';
import { he } from '../i18n/he';

const orderTones = {
  NEW: 'blue',
  IN_PROGRESS: 'amber',
  QUOTED: 'green',
  CLOSED: 'slate',
  CANCELLED: 'red',
} as const satisfies Record<OrderStatus, BadgeTone>;

const customerTones = {
  ACTIVE: 'green',
  DISABLED: 'red',
} as const satisfies Record<CustomerStatus, BadgeTone>;

const productTones = {
  DRAFT: 'slate',
  ACTIVE: 'green',
  HIDDEN: 'amber',
  ARCHIVED: 'red',
} as const satisfies Record<ProductStatus, BadgeTone>;

const contentTones = {
  SOURCE_PENDING: 'amber',
  AI_GENERATED: 'blue',
  APPROVED: 'green',
  TENANT_EDITED: 'green',
} as const satisfies Record<ContentState, BadgeTone>;

export function OrderStatusBadge({ status }: { status: OrderStatus }) {
  return (
    <StatusBadge value={status} tones={orderTones} labels={he.orderStatus} testId="order-status" />
  );
}

export function CustomerStatusBadge({ status }: { status: CustomerStatus }) {
  return (
    <StatusBadge
      value={status}
      tones={customerTones}
      labels={he.customerStatus}
      testId="customer-status"
    />
  );
}

/** An ACTIVE product that shoppers do not see (adr/0046) says why instead of "shown in the store". */
export function ProductStatusBadge({
  status,
  hiddenFromShoppers = null,
}: {
  status: ProductStatus;
  hiddenFromShoppers?: ShopperHiddenReason | null;
}) {
  if (status === 'ACTIVE' && hiddenFromShoppers !== null) {
    return (
      <span data-testid="product-status" data-status={hiddenFromShoppers}>
        <Badge tone="amber">{he.shopperHidden[hiddenFromShoppers]}</Badge>
      </span>
    );
  }
  return (
    <StatusBadge
      value={status}
      tones={productTones}
      labels={he.productStatus}
      testId="product-status"
    />
  );
}

/** The state of a product's marketing copy, on the product's own screen. */
export function ContentStateBadge({ state }: { state: ContentState }) {
  return (
    <StatusBadge
      value={state}
      tones={contentTones}
      labels={he.contentState}
      testId="content-state"
    />
  );
}

export function ActiveBadge({ active }: { active: boolean }) {
  return (
    <span data-testid="active" data-active={String(active)}>
      <Badge tone={active ? 'green' : 'slate'}>
        {active ? he.common.active : he.common.inactive}
      </Badge>
    </span>
  );
}
